<template>
  <div class="py-6 container-fluid">
    <!-- Header -->
    <div class="d-flex justify-content-between align-items-center mb-4 fade-in">
      <div>
        <h3 class="font-serif text-dark mb-1">訂單管理</h3>
        <p
          class="text-xs text-muted font-weight-bold text-uppercase tracking-wide"
        >
          管理與追蹤您的所有訂單
        </p>
      </div>
    </div>

    <!-- Stats Dashboard -->
    <div class="row mb-4 fade-in delay-1">
      <div class="col-xl-3 col-sm-6 mb-xl-0 mb-4">
        <div class="card h-100 shadow-sm border-0">
          <div class="card-body p-3">
            <div class="row">
              <div class="col-8">
                <div class="numbers">
                  <p
                    class="text-sm mb-0 text-capitalize font-weight-bold opacity-7"
                  >
                    新訂單
                  </p>
                  <h5 class="font-weight-bolder font-serif mb-0 text-primary">
                    {{ stats.new_orders || 0 }}
                  </h5>
                </div>
              </div>
              <div class="col-4 text-end">
                <div
                  class="icon icon-shape bg-primary shadow text-center border-radius-md"
                >
                  <i
                    class="ni ni-cart text-lg opacity-10"
                    aria-hidden="true"
                  ></i>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
      <div class="col-xl-3 col-sm-6 mb-xl-0 mb-4">
        <div class="card h-100 shadow-sm border-0">
          <div class="card-body p-3">
            <div class="row">
              <div class="col-8">
                <div class="numbers">
                  <p
                    class="text-sm mb-0 text-capitalize font-weight-bold opacity-7"
                  >
                    待確認 (付款)
                  </p>
                  <h5 class="font-weight-bolder font-serif mb-0 text-warning">
                    {{ stats.pending_confirmation || 0 }}
                  </h5>
                </div>
              </div>
              <div class="col-4 text-end">
                <div
                  class="icon icon-shape bg-warning shadow text-center border-radius-md"
                >
                  <i
                    class="ni ni-money-coins text-lg opacity-10"
                    aria-hidden="true"
                  ></i>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
      <div class="col-xl-3 col-sm-6 mb-xl-0 mb-4">
        <div class="card h-100 shadow-sm border-0">
          <div class="card-body p-3">
            <div class="row">
              <div class="col-8">
                <div class="numbers">
                  <p
                    class="text-sm mb-0 text-capitalize font-weight-bold opacity-7"
                  >
                    待出貨
                  </p>
                  <h5 class="font-weight-bolder font-serif mb-0 text-info">
                    {{ stats.to_ship || 0 }}
                  </h5>
                </div>
              </div>
              <div class="col-4 text-end">
                <div
                  class="icon icon-shape bg-info shadow text-center border-radius-md"
                >
                  <i
                    class="ni ni-delivery-fast text-lg opacity-10"
                    aria-hidden="true"
                  ></i>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
      <div class="col-xl-3 col-sm-6">
        <div class="card h-100 shadow-sm border-0">
          <div class="card-body p-3">
            <div class="row">
              <div class="col-8">
                <div class="numbers">
                  <p
                    class="text-sm mb-0 text-capitalize font-weight-bold opacity-7"
                  >
                    已完成
                  </p>
                  <h5 class="font-weight-bolder font-serif mb-0 text-success">
                    {{ stats.completed || 0 }}
                  </h5>
                </div>
              </div>
              <div class="col-4 text-end">
                <div
                  class="icon icon-shape bg-success shadow text-center border-radius-md"
                >
                  <i
                    class="ni ni-check-bold text-lg opacity-10"
                    aria-hidden="true"
                  ></i>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>

    <!-- Filters & Tabs -->
    <div class="card border-0 shadow-sm mb-4 fade-in delay-1">
      <div class="card-header border-bottom py-3">
        <ul class="nav nav-tabs card-header-tabs">
          <li class="nav-item">
            <a
              class="nav-link"
              :class="{ active: filters.tab === 'all' || !filters.tab }"
              href="#"
              @click.prevent="setTab('all')"
              >全部此單</a
            >
          </li>
          <li class="nav-item">
            <a
              class="nav-link"
              :class="{ active: filters.tab === 'new' }"
              href="#"
              @click.prevent="setTab('new')"
              >新進訂單
              <span v-if="stats.new_orders > 0" class="badge bg-primary ms-1">{{
                stats.new_orders
              }}</span>
            </a>
          </li>
          <li class="nav-item">
            <a
              class="nav-link"
              :class="{ active: filters.tab === 'pending_confirmation' }"
              href="#"
              @click.prevent="setTab('pending_confirmation')"
            >
              待處理/付款
              <span
                v-if="stats.pending_confirmation > 0"
                class="badge bg-warning ms-1"
                >{{ stats.pending_confirmation }}</span
              >
            </a>
          </li>
          <li class="nav-item">
            <a
              class="nav-link"
              :class="{ active: filters.tab === 'to_ship' }"
              href="#"
              @click.prevent="setTab('to_ship')"
            >
              待出貨
              <span v-if="stats.to_ship > 0" class="badge bg-info ms-1">{{
                stats.to_ship
              }}</span>
            </a>
          </li>
          <li class="nav-item">
            <a
              class="nav-link"
              :class="{ active: filters.tab === 'shipped' }"
              href="#"
              @click.prevent="setTab('shipped')"
            >
              已出貨
              <span v-if="stats.shipped > 0" class="badge bg-primary ms-1">{{
                stats.shipped
              }}</span>
            </a>
          </li>
          <li class="nav-item">
            <a
              class="nav-link"
              :class="{ active: filters.tab === 'completed' }"
              href="#"
              @click.prevent="setTab('completed')"
              >已完成</a
            >
          </li>
          <li class="nav-item">
            <a
              class="nav-link"
              :class="{ active: filters.tab === 'refund_process' }"
              href="#"
              @click.prevent="setTab('refund_process')"
              >退貨/退款</a
            >
          </li>
          <li class="nav-item">
            <a
              class="nav-link"
              :class="{ active: filters.tab === 'cancelled' }"
              href="#"
              @click.prevent="setTab('cancelled')"
              >已取消</a
            >
          </li>
        </ul>
      </div>
      <div class="card-body p-4">
        <div class="row align-items-end g-3">
          <div class="col-md-4">
            <label
              class="form-label text-xs uppercase font-weight-bold text-muted"
              >搜尋訂單編號 / 客戶</label
            >
            <input
              v-model="filters.search"
              class="form-control"
              placeholder="搜尋 ID, 姓名或 Email"
            />
          </div>
          <!-- Removed Status Select -->
          <div class="col-md-4">
            <label
              class="form-label text-xs uppercase font-weight-bold text-muted"
              >日期範圍</label
            >
            <div class="d-flex gap-2">
              <input
                type="date"
                class="form-control"
                v-model="filters.start_date"
              />
              <input
                type="date"
                class="form-control"
                v-model="filters.end_date"
              />
            </div>
          </div>
          <div class="col-md-4 d-flex justify-content-end gap-2">
            <button
              class="btn btn-outline-secondary btn-sm text-uppercase px-4"
              @click="resetFilters"
            >
              重置
            </button>
            <button
              class="btn btn-dark btn-sm text-uppercase px-4"
              @click="fetchOrders(1)"
            >
              篩選
            </button>
          </div>
        </div>
      </div>
    </div>

    <!-- Table -->
    <div class="card border-0 shadow-sm fade-in delay-2">
      <div class="card-body px-0 pt-0 pb-2">
        <div class="table-responsive p-0">
          <table class="table align-items-center mb-0">
            <thead class="bg-gray-50">
              <tr>
                <th
                  class="text-uppercase text-secondary text-xxs font-weight-bolder opacity-7 ps-4"
                >
                  訂單編號
                </th>
                <th
                  class="text-uppercase text-secondary text-xxs font-weight-bolder opacity-7 ps-2"
                >
                  狀態
                </th>
                <th
                  class="text-center text-uppercase text-secondary text-xxs font-weight-bolder opacity-7"
                >
                  總計
                </th>
                <th
                  class="text-center text-uppercase text-secondary text-xxs font-weight-bolder opacity-7"
                >
                  日期
                </th>
                <th
                  class="text-center text-uppercase text-secondary text-xxs font-weight-bolder opacity-7"
                >
                  客戶
                </th>
                <th class="text-secondary opacity-7"></th>
              </tr>
            </thead>
            <tbody>
              <tr v-if="orders.length === 0">
                <td colspan="6" class="text-center py-5 text-muted">
                  無訂單資料。
                </td>
              </tr>
              <tr
                v-for="order in orders"
                :key="order.id"
                class="group-hover:bg-gray-50 transition-colors"
              >
                <td class="ps-4">
                  <div class="d-flex flex-column">
                    <h6 class="mb-0 text-sm font-monospace text-dark">
                      #{{ order.order_number }}
                    </h6>
                    <div class="d-flex align-items-center gap-2">
                      <span class="text-xs text-secondary"
                        >{{ order.items_count }} 項商品</span
                      >
                      <span
                        v-if="order.unread_messages_count > 0"
                        class="badge bg-danger rounded-pill"
                        style="font-size: 0.6rem"
                      >
                        {{ order.unread_messages_count }} 未讀
                      </span>
                    </div>
                  </div>
                </td>
                <td>
                  <span
                    class="badge badge-sm border"
                    :class="getStatusBadgeClass(order.status)"
                  >
                    {{ formatStatus(order.status) }}
                  </span>
                  <div v-if="order.sub_status" class="mt-1">
                    <span class="badge bg-gradient-secondary text-xxs">
                      {{ formatSubStatus(order.sub_status) }}
                    </span>
                  </div>
                  <div v-if="hasOversoldItems(order)" class="mt-1">
                    <span class="badge bg-danger text-white text-xxs">
                      <i class="fas fa-exclamation-circle me-1"></i>超賣/預購
                    </span>
                  </div>
                  <div v-if="order.refund_status" class="mt-1">
                    <span class="badge bg-gradient-danger text-white text-xxs">
                      <i class="fas fa-undo-alt me-1"></i
                      >{{ formatRefundStatus(order.refund_status) }}
                    </span>
                  </div>
                </td>
                <td class="align-middle text-center">
                  <span class="text-sm font-weight-bold font-serif"
                    >NT$ {{ Number(order.total_amount).toLocaleString() }}</span
                  >
                  <div class="text-xs text-muted" v-if="order.payment_status">
                    {{ formatPaymentStatus(order.payment_status) }}
                  </div>
                  <div class="text-xs text-muted" v-if="order.refund_status">
                    {{ formatRefundStatus(order.refund_status) }}
                  </div>
                </td>
                <td class="align-middle text-center">
                  <span class="text-xs font-weight-bold text-secondary">{{
                    new Date(order.created_at).toLocaleDateString()
                  }}</span>
                  <p class="text-xs text-secondary mb-0">
                    {{ new Date(order.created_at).toLocaleTimeString() }}
                  </p>
                </td>
                <td class="align-middle text-center">
                  <p class="text-xs font-weight-bold mb-0 text-dark">
                    {{ order.buyer?.name || "Guest" }}
                  </p>
                  <p class="text-xs text-secondary mb-0">
                    {{ order.buyer?.email }}
                  </p>
                </td>
                <td class="align-middle text-end pe-4">
                  <button
                    class="btn btn-link text-dark text-xs mb-0 px-2"
                    @click="$router.push(`/orders/${order.order_number}`)"
                    v-if="can('orders.view')"
                  >
                    查看詳情 <i class="fas fa-arrow-right ms-1"></i>
                  </button>
                </td>
              </tr>
            </tbody>
          </table>
        </div>

        <!-- Pagination -->
        <div
          class="d-flex justify-content-center py-4 border-top"
          v-if="pagination.total > 0"
        >
          <soft-pagination
            color="dark"
            :current="pagination.current_page"
            :total="pagination.last_page"
            @change="fetchOrders"
          />
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import SoftPagination from "@/components/SoftPagination.vue";
import request from "@/utils/request";
import { hasPermission } from "@/utils/permissions";
// ... other imports

export default {
  name: "Orders",
  components: { SoftPagination },
  data() {
    return {
      orders: [],
      loading: false,
      stats: {
        new_orders: 0,
        pending_confirmation: 0,
        to_ship: 0,
        shipped: 0,
        completed: 0,
      },
      filters: {
        search: "",
        tab: "all",
        seller_email: "",
        start_date: "",
        end_date: "",
      },
      pagination: { current_page: 1, last_page: 1, total: 0 },
      currentUser: JSON.parse(localStorage.getItem("user") || "{}"),
    };
  },
  methods: {
    can(permission) {
      return hasPermission(this.currentUser, permission);
    },
    // ... kept helper methods ...
    getStatusBadgeClass(status) {
      const map = {
        completed: "bg-success-subtle text-success border-success-subtle",
        shipped: "bg-info-subtle text-info border-info-subtle",
        pending: "bg-warning-subtle text-warning border-warning-subtle",
        cancelled: "bg-danger-subtle text-danger border-danger-subtle",
        processing: "bg-primary-subtle text-primary border-primary-subtle",
      };
      return map[status] || "bg-light text-dark";
    },
    formatStatus(s) {
      if (!s) return "";
      const map = {
        pending: "待處理",
        processing: "處理中",
        shipped: "已出貨",
        completed: "已完成",
        cancelled: "已取消",
        pending_payment: "待付款",
        paid: "已付款",
        failed: "付款失敗",
        refunded: "已退款",
        confirmed: "已確認",
      };
      return map[s] || s;
    },
    formatSubStatus(status) {
      if (!status) return "";
      const subStatusMap = {
        product_pending_confirmation: "商品待確認",
        platform_pending_shipment: "平台待出貨",
        seller_pending_shipment: "賣家待出貨",
        pending_receipt: "待收貨",
        pending_review: "待評價",
        platform_verifying: "平台驗證中",
        verification_failed: "驗證失敗",
        missing_items_overdue: "缺件逾期",
        seller_shipped: "賣家已出貨",
        payment_pending_confirmation: "付款待確認",
        payment_declined: "付款被退回",
        picked_up: "已取貨",
        platform_shipped: "平台已出貨",
        PLATFORM_SHIPPED: "平台已出貨",
        // PayUni Logistics Statuses
        logistics_processing: "物流處理中",
        in_transit: "運送中",
        arrived_at_store: "已送達門市",
        delivery_abnormal: "配送異常",
        cancelled_by_logistics: "物流取消",
        logistics_lost: "包裹遺失",
        buyer_not_picked_up: "買家逾期未取",
        returned_to_seller: "已退回賣家",
        returned_to_store_seller_not_picked_up: "退回門市 (賣家未取)",
        returning_via_delivery: "退回賣家中 (宅配)",
        store_closed_redirect_needed: "門市關轉 (需重選)",
      };
      return subStatusMap[status] || status;
    },
    hasOversoldItems(order) {
      if (!order.items) return false;
      return order.items.some((item) => item.product && item.product.stock < 0);
    },
    formatPaymentStatus(s) {
      const map = {
        pending: "未付款",
        paid: "已付款",
        failed: "失敗",
        refunded: "已退款",
        confirmed: "已確認",
      };
      return map[s] || s;
    },
    formatRefundStatus(status) {
      const map = {
        applying: "申請退款",
        accepted: "退款已受理",
        waiting_return: "待退貨",
        checking: "退貨確認中",
        refunded: "已退款",
        cancelled: "退款已取消",
      };
      return map[status] || status;
    },
    async fetchStats() {
      try {
        const res = await request.get("/api/admin/orders/stats");
        this.stats = res.data;
      } catch (e) {
        console.error(e);
      }
    },
    setTab(tab) {
      this.filters.tab = tab;
      this.filters.page = 1;
      this.fetchOrders(1);
    },
    async fetchOrders(page = 1) {
      this.loading = true;
      try {
        const params = { page, ...this.filters };
        // Remove empty filters
        Object.keys(params).forEach((k) => !params[k] && delete params[k]);

        const res = await request.get("/api/admin/orders", { params });
        this.orders = res.data.data;
        this.pagination = res.data.meta;

        // Refresh stats on load
        this.fetchStats();
      } catch (e) {
        console.error(e);
      } finally {
        this.loading = false;
      }
    },
    resetFilters() {
      this.filters = {
        search: "",
        tab: "all",
        seller_email: "",
        start_date: "",
        end_date: "",
      };
      this.fetchOrders(1);
    },
  },
  created() {
    this.fetchOrders();
  },
};
</script>

<style scoped>
.font-serif {
  font-family: "Noto Serif TC", serif;
}
.fade-in {
  animation: fadeIn 0.5s ease-out forwards;
  opacity: 0;
  transform: translateY(10px);
}
.delay-1 {
  animation-delay: 0.1s;
}
.delay-2 {
  animation-delay: 0.2s;
}
@keyframes fadeIn {
  to {
    opacity: 1;
    transform: translateY(0);
  }
}
</style>
